import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Button,
    Grid,
    TextField,
    Typography,
    IconButton,
    Paper,
} from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    dayContainer: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
    },
    title: {
        marginBottom: theme.spacing(2),
    },
    dayTitle: {
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
    },
}));

const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const ProfessionalSchedulesForm = ({ initialSchedules, onChange }) => {
    const classes = useStyles();
    // Transform array of objects to keyed object for easier editing
    const [schedules, setSchedules] = useState([]);

    useEffect(() => {
        if (initialSchedules && initialSchedules.length > 0) {
            setSchedules(initialSchedules);
        } else {
            // Init with empty schedules for all days or allow adding?
            // Let's allow adding via "Add Schedule" button per day or just list all days?
            // UI design: List all days. If fields are empty, it means closed.
            const defaultSchedules = daysOfWeek.map(day => ({
                dayOfWeek: day,
                startTime: "08:00",
                endTime: "18:00",
                lunchStartTime: "12:00",
                lunchEndTime: "13:00",
                active: false
            }));
            setSchedules(defaultSchedules);
        }
    }, [initialSchedules]);

    const handleChange = (index, field, value) => {
        const newSchedules = [...schedules];
        newSchedules[index] = { ...newSchedules[index], [field]: value };
        setSchedules(newSchedules);
        onChange(newSchedules.filter(s => s.active));
    };

    const handleToggleDay = (index) => {
        const newSchedules = [...schedules];
        newSchedules[index] = { ...newSchedules[index], active: !newSchedules[index].active };
        setSchedules(newSchedules);
        onChange(newSchedules.filter(s => s.active));
    }

    const translateDay = (day) => {
        const days = {
            Monday: i18n.t("professionalModal.schedules.mon") || "Lunes",
            Tuesday: i18n.t("professionalModal.schedules.tue") || "Martes",
            Wednesday: i18n.t("professionalModal.schedules.wed") || "Miércoles",
            Thursday: i18n.t("professionalModal.schedules.thu") || "Jueves",
            Friday: i18n.t("professionalModal.schedules.fri") || "Viernes",
            Saturday: i18n.t("professionalModal.schedules.sat") || "Sábado",
            Sunday: i18n.t("professionalModal.schedules.sun") || "Domingo"
        }
        return days[day] || day;
    }

    return (
        <div className={classes.root}>
            <Typography variant="h6" className={classes.title}>
                {i18n.t("professionalModal.schedules.title") || "Horarios de Atención"}
            </Typography>

            {schedules.map((schedule, index) => (
                <Paper key={schedule.dayOfWeek} className={classes.dayContainer} variant="outlined">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <Typography className={classes.dayTitle}>
                                {translateDay(schedule.dayOfWeek)}
                            </Typography>
                            <Button
                                size="small"
                                variant={schedule.active ? "contained" : "outlined"}
                                color={schedule.active ? "primary" : "default"}
                                onClick={() => handleToggleDay(index)}
                            >
                                {schedule.active ? (i18n.t("common.enabled") || "Abierto") : (i18n.t("common.disabled") || "Cerrado")}
                            </Button>
                        </Grid>

                        {schedule.active && (
                            <>
                                <Grid item xs={6} sm={2}>
                                    <TextField
                                        label={i18n.t("professionalModal.schedules.start") || "Inicio"}
                                        type="time"
                                        value={schedule.startTime}
                                        onChange={(e) => handleChange(index, "startTime", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }} // 5 min
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <TextField
                                        label={i18n.t("professionalModal.schedules.end") || "Fin"}
                                        type="time"
                                        value={schedule.endTime}
                                        onChange={(e) => handleChange(index, "endTime", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <TextField
                                        label={i18n.t("professionalModal.schedules.lunchStart") || "Inicio Almuerzo"}
                                        type="time"
                                        value={schedule.lunchStartTime}
                                        onChange={(e) => handleChange(index, "lunchStartTime", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <TextField
                                        label={i18n.t("professionalModal.schedules.lunchEnd") || "Fin Almuerzo"}
                                        type="time"
                                        value={schedule.lunchEndTime}
                                        onChange={(e) => handleChange(index, "lunchEndTime", e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }}
                                        fullWidth
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Paper>
            ))}
        </div>
    );
};

export default ProfessionalSchedulesForm;
